import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Checkbox, Form, Input, Modal, Pagination, Popconfirm,
  Select, Space, Table, Tag, Tooltip,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { ColumnsType } from 'antd/lib/table';
import BreadCrumbs from '../../../common/components/Breadcrumbs';
import { Action, Page } from '../../../common/constants/pageAction';
import ability from '../../../common/ability';
import snAiRulesService, { ISnAiRule } from '../../../services/cmdb/snAiRules/snAiRules.service';

const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_COLORS: Record<string, string> = {
  manual: 'blue',
  synthesized: 'purple',
  protected: 'red',
};

const SnAiRules: React.FC = () => {
  const [data, setData] = useState<ISnAiRule[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [editTarget, setEditTarget] = useState<ISnAiRule | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await snAiRulesService.list(page, limit);
      setData(result.data);
      setTotal(result.count);
    } catch { /* handled by request interceptor */ } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditTarget(null);
    form.resetFields();
    form.setFieldsValue({ category: 'manual', is_active: true });
    setModalVisible(true);
  };

  const openEdit = (rule: ISnAiRule) => {
    setEditTarget(rule);
    form.setFieldsValue({
      rule_text: rule.rule_text,
      category: rule.category ?? 'manual',
      is_active: rule.is_active,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editTarget) {
        await snAiRulesService.update(editTarget.id, values.rule_text, values.is_active, values.category);
        toast.success('Rule updated.');
      } else {
        await snAiRulesService.create(values.rule_text, values.category);
        toast.success('Rule created.');
      }
      setModalVisible(false);
      load();
    } catch { /* handled by request interceptor */ } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await snAiRulesService.remove(id);
      toast.success('Rule deleted.');
      load();
    } catch { /* handled by request interceptor */ }
  };

  const columns: ColumnsType<ISnAiRule> = [
    {
      title: 'Rule',
      dataIndex: 'rule_text',
      key: 'rule_text',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (val: string) => (
        <Tag color={CATEGORY_COLORS[val] ?? 'default'}>{val ?? '—'}</Tag>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (val: boolean) => <Tag color={val ? 'success' : 'default'}>{val ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (val: string) => new Date(val).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          {ability.can(Action.Update, Page.CmdbSnAiRules) && (
            <Tooltip title="Edit">
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
            </Tooltip>
          )}
          {ability.can(Action.Delete, Page.CmdbSnAiRules) && (
            <Popconfirm title="Delete this rule?" onConfirm={() => handleDelete(record.id)}>
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="sqlServer">
      <div className="title-block">
        <h4 className="p-0">
          <BreadCrumbs pageName={Page.CmdbSnAiRules} />
        </h4>
      </div>

      <div className="main-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          {ability.can(Action.Add, Page.CmdbSnAiRules) && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
              Add Rule
            </Button>
          )}
        </div>

        <Table<ISnAiRule>
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No rules defined.' }}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={limit}
            total={total}
            showSizeChanger={false}
            showTotal={(t) => `${t} rules`}
            onChange={setPage}
          />
        </div>
      </div>

      <Modal
        title={editTarget ? 'Edit Rule' : 'Add Rule'}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        okText="Save"
        okButtonProps={{ loading: saving }}
        centered
        destroyOnClose
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="rule_text"
            label="Rule"
            rules={[{ required: true, message: 'Rule text is required' }]}
          >
            <TextArea rows={4} maxLength={4000} showCount />
          </Form.Item>
          <Form.Item name="category" label="Category">
            <Select>
              <Option value="manual">Manual</Option>
              <Option value="synthesized">Synthesized</Option>
              <Option value="protected">Protected</Option>
            </Select>
          </Form.Item>
          {editTarget && (
            <Form.Item name="is_active" valuePropName="checked">
              <Checkbox>Active</Checkbox>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default SnAiRules;
